import { TOffer, TOfferCalculated, TOfferCompressed } from "types";

// Die Struktur Deiner Rohdaten (ergänzt um Laufzeit und Zinslast)
// export interface FinancingOffer {
//   apr: number;
//   borrowingRate: number;
//   closingFee: number;
//   closingRate: number;
//   debitType: number;
//   downPayment: number;
//   interestLoad?: number; // Neu: Zinslast (inkl. versteckter Kosten)
//   lessKm: number;
//   mileage: number;
//   moreKm: number;
//   netLoanAmount: number;
//   rate: number;
//   total: number;
// }

/**
 * SCHRITT 1: NORMALISIERUNG (Anzahlung auf 0 setzen)
 * Überführt das fehlerhafte Roh-Angebot in einen sauberen Basis-Datensatz ohne Anzahlung.
 */
export const normalizeToZeroDownPayment = (
  offer: TOffer,
  runtime: number,
): TOfferCalculated => {
  const annuityFactor = getAnnuityFactor(offer.borrowingRate, runtime);

  // 1. Wie viel Rate hat uns die bisherige Anzahlung gespart?
  const savedRate = offer.downPayment * annuityFactor;

  // 2. Neue Basis-Werte berechnen (Anzahlung rausrechnen)
  const baseRate = offer.rate + savedRate;
  const baseNetLoanAmount = offer.netLoanAmount + offer.downPayment;

  // 3. Fehlerhaftes Total reparieren: (Rate * Laufzeit) + Schlussrate + Abschlussgebühr
  const correctTotal =
    baseRate * runtime + offer.closingRate + offer.closingFee;

  // 4. Zinslast berechnen (Was zahlt der Kunde mehr, als er sich leiht?)
  const interestLoad = correctTotal - baseNetLoanAmount;

  const normalizedOffer = {
    ...offer,
    downPayment: 0,
    runtime,
    originalRate: offer.rate,
    rate: Math.round(baseRate * 100) / 100,
    netLoanAmount: Math.round(baseNetLoanAmount * 100) / 100,
    total: Math.round(correctTotal * 100) / 100,
    interestLoad: Math.round(interestLoad * 100) / 100,
  };
  return normalizedOffer;
};

/**
 * SCHRITT 2: NEUBERECHNUNG (Für Deinen variablen Ratenrechner)
 * Nimmt das normalisierte Basis-Angebot (0€ Anzahlung) und berechnet es für jede beliebige Anzahlung.
 */
export const recalculateForDownPayment = (
  normalizedOffer: TOffer,
  newDownPayment: number,
  runtime: number,
): TOfferCalculated => {
  const annuityFactor = getAnnuityFactor(
    normalizedOffer.borrowingRate,
    runtime,
  );

  // 1. Wie viel Rate spart die NEUE Anzahlung?
  const rateReduction = newDownPayment * annuityFactor;

  // 2. Werte anpassen
  const newRate = normalizedOffer.rate - rateReduction;
  const newNetLoanAmount = normalizedOffer.netLoanAmount - newDownPayment;

  // 3. Total und Zinslast neu berechnen
  const newTotal =
    newRate * runtime +
    normalizedOffer.closingRate +
    normalizedOffer.closingFee;
  const newInterestLoad = newTotal - newNetLoanAmount;

  return {
    ...normalizedOffer,
    runtime,
    originalRate: normalizedOffer.rate,
    downPayment: newDownPayment,
    rate: Math.round(newRate * 100) / 100,
    netLoanAmount: Math.round(newNetLoanAmount * 100) / 100,
    total: Math.round(newTotal * 100) / 100,
    interestLoad: Math.round(newInterestLoad * 100) / 100,
  };
};

/**
 * HILFSFUNKTION: Berechnet den Annuitätenfaktor.
 * Gibt an, wie viel monatliche Rate 1 Euro Kreditbetrag bei gegebenem Zins und Laufzeit kostet.
 */
const getAnnuityFactor = (borrowingRate: number, runtime: number): number => {
  if (borrowingRate === 0) return 1 / runtime;
  const i = borrowingRate / 100 / 12; // Monatlicher Zins
  const qToN = Math.pow(1 + i, runtime); // (1+i)^n
  return (i * qToN) / (qToN - 1);
};

export const calculateMaxDownPayment = (
  normalizedOffer: TOffer,
  minRate: number,
  runtime: number,
): number => {
  // 1. Wenn die Basis-Rate (ohne Anzahlung) ohnehin schon kleiner oder gleich
  // der Minimalrate ist, darf gar keine Anzahlung geleistet werden.
  if (normalizedOffer.rate <= minRate) {
    return 0;
  }

  const annuityFactor = getAnnuityFactor(
    normalizedOffer.borrowingRate,
    runtime,
  );

  // 2. Wie viel "Raten-Budget" dürfen wir durch die Anzahlung vernichten?
  // Beispiel: Basisrate 157,50 € - Minimalrate 49,00 € = 108,50 € erlaubte Reduktion
  const allowableRateReduction = normalizedOffer.rate - minRate;

  // 3. Umrechnen der Ratenreduktion in die dafür nötige Anzahlung
  let maxDownPayment = allowableRateReduction / annuityFactor;

  // 4. LOGISCHE OBERGRENZE (Bank-Regel):
  // Man darf bei Ballonfinanzierungen nie so viel anzahlen, dass der verbleibende
  // Kreditbetrag kleiner wird als die festgelegte Schlussrate.
  // Max Anzahlung = Gesamter Kreditbedarf (ohne Anzahlung) minus Schlussrate.
  const absoluteMaxDownPayment = Math.max(
    0,
    normalizedOffer.netLoanAmount - normalizedOffer.closingRate,
  );

  // 5. Wir nehmen den kleineren der beiden Werte (entweder greift das Raten-Limit oder das Bank-Limit)
  if (maxDownPayment > absoluteMaxDownPayment) {
    maxDownPayment = absoluteMaxDownPayment;
  }

  // Abrunden auf ganze Euro (oder auf 2 Nachkommastellen mit Math.floor(max * 100) / 100),
  // um sicherzugehen, dass wir die Minimalrate nicht durch Aufrunden versehentlich leicht unterschreiten.
  return Math.floor(maxDownPayment);
};

export const recalculateForDownPaymentFast = (
  normalizedOffer: TOffer,
  newDownPayment: number,
  runtime: number,
) => {
  // 1. Lineare Rate Reduction ohne Zinseszins
  const rateReduction = runtime > 0 ? newDownPayment / runtime : 0;

  // 2. Neue Rate berechnen
  const newRate = normalizedOffer.rate - rateReduction;

  // 3. Fallback-Return mit den minimal bekannten Werten
  return {
    ...normalizedOffer,
    runtime,
    originalRate: normalizedOffer.rate,
    downPayment: newDownPayment,
    rate: Math.round(newRate * 100) / 100,
  };
};
